export type NotificationScope =
  | 'org'
  | 'project'
  | 'feature'
  | 'release'
  | 'suite'
  | 'test'
  | 'execution'
  | 'build'
  | 'upload'
  | 'results'
  | 'all';

export type NotificationPayload = {
  scope?: string;
  orgId?: string | null;
  projectId?: string | null;
  uploadId?: string;
  fileName?: string;
  status?: 'queued' | 'processing' | 'completed' | 'completed_with_errors' | 'failed';
  error?: string | null;
  userId?: string;
};

export const normalizeScope = (scope: string | undefined): NotificationScope => {
  if (!scope) return 'project';
  switch (scope) {
    case 'org':
    case 'project':
    case 'feature':
    case 'release':
    case 'suite':
    case 'test':
    case 'execution':
    case 'build':
    case 'upload':
    case 'results':
    case 'all':
      return scope;
    default:
      return 'project';
  }
};

const parseNotificationPayload = (data: string): NotificationPayload | null => {
  try {
    const parsed: unknown = JSON.parse(data);
    if (!parsed || typeof parsed !== 'object') return null;
    const record = parsed as Record<string, unknown>;
    const scope = typeof record.scope === 'string' ? record.scope : undefined;
    const orgId =
      typeof record.orgId === 'string' ? record.orgId : record.orgId === null ? null : undefined;
    const projectId =
      typeof record.projectId === 'string'
        ? record.projectId
        : record.projectId === null
          ? null
          : undefined;
    const uploadId = typeof record.uploadId === 'string' ? record.uploadId : undefined;
    const fileName = typeof record.fileName === 'string' ? record.fileName : undefined;
    const status =
      record.status === 'queued' ||
      record.status === 'processing' ||
      record.status === 'completed' ||
      record.status === 'completed_with_errors' ||
      record.status === 'failed'
        ? record.status
        : undefined;
    const error =
      typeof record.error === 'string' ? record.error : record.error === null ? null : undefined;
    const userId = typeof record.userId === 'string' ? record.userId : undefined;
    return { scope, orgId, projectId, uploadId, fileName, status, error, userId };
  } catch {
    return null;
  }
};

type NotificationsStreamHandlers = {
  onInvite: () => void;
  onDataChanged: (payload: NotificationPayload) => void;
  onUpload: (payload: NotificationPayload) => void;
};

export type StreamTokenProvider = (orgId: string | null) => Promise<string>;

export const createNotificationsStream = async ({
  apiBaseUrl,
  orgId,
  getStreamToken,
  handlers,
}: {
  apiBaseUrl: string;
  orgId: string | null;
  getStreamToken: StreamTokenProvider;
  handlers: NotificationsStreamHandlers;
}): Promise<{ close: () => void }> => {
  let source: EventSource | null = null;
  let closed = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectDelayMs = 1000;
  let connectGeneration = 0;

  const handleInvite = (): void => {
    handlers.onInvite();
  };
  const handleDataChanged = (event: MessageEvent): void => {
    if (typeof event.data !== 'string') return;
    const payload = parseNotificationPayload(event.data);
    if (!payload) return;
    handlers.onDataChanged(payload);
  };
  const handleUpload = (event: MessageEvent): void => {
    if (typeof event.data !== 'string') return;
    const payload = parseNotificationPayload(event.data);
    if (!payload) return;
    handlers.onUpload(payload);
  };

  const clearReconnectTimer = (): void => {
    if (!reconnectTimer) return;
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  };

  const cleanupSource = (): void => {
    if (!source) return;
    source.removeEventListener('invite', handleInvite);
    source.removeEventListener('data_changed', handleDataChanged);
    source.removeEventListener('upload', handleUpload);
    source.onopen = null;
    source.onerror = null;
    source.close();
    source = null;
  };

  const scheduleReconnect = (): void => {
    if (closed || reconnectTimer) return;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void connect();
    }, reconnectDelayMs);
    reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30_000);
  };

  const connect = async (): Promise<void> => {
    if (closed) return;
    const generation = ++connectGeneration;
    try {
      const streamToken = await getStreamToken(orgId);
      if (generation !== connectGeneration) return;
      const url = new URL('/notifications/stream', apiBaseUrl);
      url.searchParams.set('token', streamToken);
      cleanupSource();
      source = new EventSource(url.toString());
      source.addEventListener('invite', handleInvite);
      source.addEventListener('data_changed', handleDataChanged);
      source.addEventListener('upload', handleUpload);
      source.onopen = () => {
        reconnectDelayMs = 1000;
      };
      source.onerror = () => {
        cleanupSource();
        scheduleReconnect();
      };
    } catch {
      scheduleReconnect();
    }
  };

  await connect();

  return {
    close: () => {
      closed = true;
      connectGeneration += 1;
      clearReconnectTimer();
      cleanupSource();
    },
  };
};
